import React, { useEffect, useState } from 'react';
import Carousel from 'react-bootstrap/Carousel';

import { AttachmentImage } from '@components/Attachment';
import Button from '@components/Button';
import Chip from '@components/Chip';

import { getIsMobile, MOBILE_MEDIA_QUERY } from '@utils/WindowUtils';

import './style.scss';

export interface ISubmissionCardViewProps {
  category: string;
  imageUrls: string[];
  description: string;
  status: 'open' | 'closed';
  followerCount?: number;
  isFollowing?: boolean | null;
  isSubmitter?: boolean | null;
  onFollowClick?: () => void;
  onUnfollowClick?: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
  onViewDetails?: () => void;
}

const SubmissionCardView = React.forwardRef<HTMLDivElement, ISubmissionCardViewProps>(
  (
    {
      category,
      imageUrls,
      description,
      status,
      followerCount = 0,
      isFollowing,
      isSubmitter,
      onFollowClick,
      onUnfollowClick,
      isSelected,
      onSelect,
      onViewDetails,
    },
    ref
  ) => {
    const isOpen = status === 'open';
    const [isMobile, setIsMobile] = useState(getIsMobile);

    useEffect(() => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return undefined;
      }
      const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
      const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);

      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', handler);

        return () => mql.removeEventListener('change', handler);
      }

      mql.addListener(handler);

      return () => mql.removeListener(handler);
    }, []);

    const statusIndicator = (
      <div className="submission-card__footer-status">
        <span className={`submission-card__status-dot public-map-marker public-map-marker--${status}`} />
        <span className={`submission-card__status-label submission-card__status-label--${status}`}>
          {isOpen ? 'Open' : 'Closed'}
        </span>
      </div>
    );

    const followSection =
      isSubmitter === true ? (
        <span className="submission-card__submitted-by-you">Submitted by you</span>
      ) : (
        <span className="submission-card__follow-info">
          <span className="submission-card__follower-count">
            {followerCount} {followerCount === 1 ? 'Follower' : 'Followers'}
          </span>
          <>
            <span className="submission-card__follow-separator" aria-hidden="true">
              &middot;
            </span>
            {isFollowing ? (
              <>
                <span className="submission-card__following-label">&#10003; Following</span>
                <span className="submission-card__follow-separator" aria-hidden="true">
                  &middot;
                </span>
                <button
                  type="button"
                  className="submission-card__unfollow-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnfollowClick?.();
                  }}
                  aria-label={`Unfollow ${category} submission`}
                >
                  Unfollow
                </button>
              </>
            ) : (
              <button
                type="button"
                className="submission-card__follow-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onFollowClick?.();
                }}
                aria-label={`Follow ${category} submission`}
              >
                Follow
              </button>
            )}
          </>
        </span>
      );

    return (
      <div ref={ref} className={`submission-card${isSelected ? ' submission-card--selected' : ''}`}>
        <button
          type="button"
          className="submission-card__select"
          onClick={isMobile ? onViewDetails : onSelect}
          aria-label={`View submission: ${category}`}
        />
        <div className="submission-card__content">
          <div className="submission-card__header">
            <Chip active>{category}</Chip>
            {isMobile ? statusIndicator : followSection}
          </div>

          <div className="submission-card__body">
            {imageUrls.length > 0 && (
              <div className="submission-card__gallery-wrapper">
                <Carousel
                  className="submission-card__gallery"
                  interval={null}
                  controls={imageUrls.length > 1}
                  indicators={imageUrls.length > 1}
                >
                  {imageUrls.map((url, index) => (
                    <Carousel.Item key={index}>
                      <AttachmentImage
                        src={url}
                        alt={`${category} photo ${index + 1}`}
                        className="submission-card__image"
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
            )}
            <p className="submission-card__description">{description}</p>
          </div>

          <div className="submission-card__footer">
            {isMobile ? (
              followSection
            ) : (
              <>
                {statusIndicator}
                <Button
                  content="View Details"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onViewDetails?.();
                  }}
                  bsStyle={ButtonStyle.PRIMARY}
                  className="submission-card__view-details"
                  aria-label="View full details of this submission"
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
);

SubmissionCardView.displayName = 'SubmissionCardView';

export default SubmissionCardView;
